import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import {
  addPlan,
  addReview,
  addThankfulness,
  addValue,
  answerPlan,
  DEFAULT_GRATITUDE_WEEKDAY,
  DEFAULT_REVIEW_PATH,
  emptyReviewState,
  gratitudeStatus,
  GRATITUDE_ITEMS,
  HONESTY_CARDS,
  MAX_VALUES,
  openPlan,
  planFor,
  planTally,
  removeValue,
  REVIEW_QUESTIONS,
  reviewFor,
} from '../../lib/review';
import { extractReviewFromFile, upsertReviewNode } from '../../lib/review_org';
import { extractContractFromFile } from '../../lib/contract_org';
import { dashboardConfigFromState, directoryOfPath, formatDatePath } from '../../lib/dashboard';

// How much of each log the page shows at once. The file holds all of it.
const LOG_SHOWN = 5;

const EMPTY_REVIEW = {
  happened: '',
  taught: '',
  differently: '',
  served: '',
  rough: false,
  affirmed: '',
};

const EMPTY_PLAN = { situation: '', action: '' };

const emptyGratitude = () =>
  Array.from({ length: GRATITUDE_ITEMS }, () => ({ thing: '', why: '' }));

const ReviewDashboard = ({
  syncBackendClient,
  storedConfig,
  reviewPath,
  gratitudeWeekday,
  contractIntegration,
}) => {
  const path = (reviewPath || '').trim() || DEFAULT_REVIEW_PATH;
  const config = useMemo(() => dashboardConfigFromState(storedConfig), [storedConfig]);

  // Fixed at mount, exactly like the other five dashboards: a page left open
  // across midnight keeps dating its entries to the day it was opened on.
  const today = useMemo(() => new Date(), []);

  const [state, setState] = useState(null);
  const [contract, setContract] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [reviewDraft, setReviewDraft] = useState(EMPTY_REVIEW);
  const [revising, setRevising] = useState(false);
  const [planDraft, setPlanDraft] = useState(EMPTY_PLAN);
  const [valueDraft, setValueDraft] = useState('');
  const [gratitudeDraft, setGratitudeDraft] = useState(emptyGratitude);

  const rawRef = useRef(null);
  const existsRef = useRef(false);
  // Chained so two quick edits become two orderly commits instead of two racing
  // ones - the GitLab commit API rejects concurrent branch updates.
  const queueRef = useRef(Promise.resolve());

  const load = useCallback(async () => {
    if (!syncBackendClient) {
      setLoadError('Not connected to a sync backend.');
      setLoaded(true);
      return;
    }

    setLoadError(null);
    setLoaded(false);

    try {
      const listing = await syncBackendClient
        .getDirectoryListing(directoryOfPath(path))
        .catch(() => null);

      const present = new Set();
      ((listing && listing.listing) || []).forEach((file) => {
        const filePath = file.get ? file.get('path') : file.path;
        if (filePath) present.add(filePath);
      });

      const contents = present.has(path) ? await syncBackendClient.getFileContents(path) : null;

      rawRef.current = contents;
      existsRef.current = contents !== null;

      setState(extractReviewFromFile(contents));
      setLoaded(true);
    } catch (error) {
      console.error('Could not read the review record:', error);
      setLoadError(error.message);
      setState(null);
      setLoaded(true);
    }
  }, [syncBackendClient, path]);

  // The one cross-dashboard read (concept §4.1): today's Tagesvertrag, shown
  // read-only so the evening reviews the promise that was actually made.
  //
  // Deliberately separate from `load` and deliberately silent on failure: the
  // contract is a nicety, this page owns only its own file, and a missing or
  // unreadable daily note must not turn into an error on the review.
  const loadContract = useCallback(async () => {
    if (!syncBackendClient || !contractIntegration || !config.dailyPathTemplate) {
      setContract(null);
      return;
    }

    try {
      const contractPath = formatDatePath(config.dailyPathTemplate, today);
      const listing = await syncBackendClient
        .getDirectoryListing(directoryOfPath(contractPath))
        .catch(() => null);

      const present = ((listing && listing.listing) || []).some((file) => {
        const filePath = file.get ? file.get('path') : file.path;
        return filePath === contractPath;
      });

      setContract(
        present
          ? extractContractFromFile(await syncBackendClient.getFileContents(contractPath))
          : null
      );
    } catch (error) {
      setContract(null);
    }
  }, [syncBackendClient, contractIntegration, config.dailyPathTemplate, today]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  useEffect(() => {
    loadContract();
  }, [loadContract, reloadToken]);

  const persist = useCallback(
    (nextState) => {
      setState(nextState);
      setSaveError(null);

      const nextContents = upsertReviewNode(rawRef.current, nextState);
      rawRef.current = nextContents;

      queueRef.current = queueRef.current.then(async () => {
        try {
          if (existsRef.current) {
            await syncBackendClient.updateFile(path, nextContents);
          } else {
            await syncBackendClient.createFile(path, nextContents);
            existsRef.current = true;
          }
        } catch (error) {
          setSaveError(`Could not save ${path}: ${error.message || error}`);
        }
      });
    },
    [syncBackendClient, path]
  );

  const canWrite = !!syncBackendClient;
  const record = useMemo(() => state || emptyReviewState(), [state]);

  const todaysReview = useMemo(() => reviewFor(record.reviews, today), [record.reviews, today]);
  const yesterdaysPlan = useMemo(() => openPlan(record.plans, today), [record.plans, today]);
  const tally = useMemo(() => planTally(record.plans), [record.plans]);
  const gratitude = useMemo(() => gratitudeStatus(record.thankfulness, today, gratitudeWeekday), [
    record.thankfulness,
    today,
    gratitudeWeekday,
  ]);
  const todaysPlan = useMemo(() => planFor(record.plans, today), [record.plans, today]);

  // Both articulation and the forward step, or nothing (concept §4.1). The gate
  // is the whole point: an entry that describes the day without saying what it
  // taught or what changes tomorrow is the half of this the evidence does not
  // support - and, worse, is the half that looks like brooding.
  const reviewComplete =
    !!reviewDraft.happened.trim() &&
    !!reviewDraft.taught.trim() &&
    !!reviewDraft.differently.trim();

  const gratitudeComplete = gratitudeDraft.some((item) => item.thing.trim());

  const handleLogReview = () => {
    if (!canWrite || !reviewComplete) return;
    persist(addReview(record, { ...reviewDraft, today }));
    setReviewDraft(EMPTY_REVIEW);
    setRevising(false);
  };

  const handleRevise = () => {
    setReviewDraft({ ...EMPTY_REVIEW, ...todaysReview });
    setRevising(true);
  };

  const handleAnswerPlan = (arose, kept) => {
    if (!canWrite || !yesterdaysPlan) return;
    persist(answerPlan(record, yesterdaysPlan.date, { arose, kept }));
  };

  const handleAddValue = () => {
    if (!canWrite || !valueDraft.trim()) return;
    persist(addValue(record, { text: valueDraft }));
    setValueDraft('');
  };

  const handleLogPlan = () => {
    if (!canWrite || !planDraft.situation.trim() || !planDraft.action.trim()) return;
    persist(addPlan(record, { ...planDraft, today }));
    setPlanDraft(EMPTY_PLAN);
  };

  const handleLogGratitude = () => {
    if (!canWrite || !gratitudeComplete) return;
    persist(addThankfulness(record, { items: gratitudeDraft, today }));
    setGratitudeDraft(emptyGratitude());
  };

  const header = (
    <div className="review__header">
      <h3 className="review__title">Tagesrückblick</h3>
      <div className="review__header__actions">
        <button className="review__header__action" onClick={() => setReloadToken((n) => n + 1)}>
          <i className="fas fa-sync-alt" /> Refresh
        </button>
        <Link className="review__header__action" to="/dashboard/contract">
          <i className="fas fa-file-signature" /> Contract
        </Link>
        <Link className="review__header__action" to="/dashboard/week">
          <i className="fas fa-chart-line" /> This week
        </Link>
        <Link className="review__header__action" to="/dashboard/focus">
          <i className="fas fa-bullseye" /> Focus
        </Link>
        <Link className="review__header__action" to="/dashboard/team">
          <i className="fas fa-users" /> Team
        </Link>
        <Link className="review__header__action" to="/dashboard/formation">
          <i className="fas fa-seedling" /> Formation
        </Link>
        <Link className="review__header__action" to="/dashboard">
          <i className="fas fa-th-large" /> Dashboard
        </Link>
      </div>
    </div>
  );

  if (!loaded) {
    return (
      <div className="review-container">
        {header}
        <div className="review__loading">
          <i className="fas fa-spinner fa-spin" /> Reading {path}...
        </div>
      </div>
    );
  }

  return (
    <div className="review-container">
      {header}

      {!!loadError && <div className="review__error">{loadError}</div>}
      {!!saveError && <div className="review__error">{saveError}</div>}

      <div className="review__intro">
        Ten minutes at the end of the day, closing the promise the Tagesvertrag opened. Every
        question here is closed and points forward on purpose: dwelling on why a day went badly
        predicts the opposite of what this page is for.
        {!state && (
          <>
            {' '}
            Nothing is written to <code>{path}</code> yet — the first entry below creates it.
          </>
        )}
      </div>

      {/* ── §4.4 (first on the page) Yesterday's if-then ────────────────────── */}

      {!!yesterdaysPlan && (
        <>
          <div className="review__section-title">
            <span>Yesterday's plan</span>
            <span className="review__section-title__note">two taps, both about an event</span>
          </div>

          <div className="review__plan review__plan--open">
            <div className="review__plan__sentence">
              <span className="review__plan__keyword">If</span> {yesterdaysPlan.situation}{' '}
              <span className="review__plan__keyword">then I will</span> {yesterdaysPlan.action}
            </div>
            <div className="review__plan__date">set {yesterdaysPlan.date}</div>

            {canWrite && (
              <div className="review__choice">
                <span className="review__choice__prompt">Did the situation arise?</span>
                <button
                  className="review__choice__option"
                  onClick={() => handleAnswerPlan('yes', 'yes')}
                >
                  yes — and I did it
                </button>
                <button
                  className="review__choice__option"
                  onClick={() => handleAnswerPlan('yes', 'no')}
                >
                  yes — and I didn't
                </button>
                <button
                  className="review__choice__option"
                  onClick={() => handleAnswerPlan('no', null)}
                >
                  it never came up
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── §4.1 The review ────────────────────────────────────────────────── */}

      <div className="review__section-title">
        <span>Today</span>
        <span className="review__section-title__note">
          {todaysReview ? 'written' : 'three questions'}
        </span>
      </div>

      {!!contract && (
        <div className="review__contract">
          <div className="review__contract__title">Today's Tagesvertrag</div>
          <ul className="review__contract__points">
            {contract.points.map((point) => (
              <li
                className={`review__contract__point${
                  point.done ? ' review__contract__point--done' : ''
                }`}
                key={point.text}
              >
                <i className={`fas ${point.done ? 'fa-check' : 'fa-minus'}`} /> {point.text}
              </li>
            ))}
          </ul>
          <div className="review__contract__note">
            Read-only — this page never writes into the contract.
          </div>
        </div>
      )}

      {todaysReview && !revising ? (
        <div className="review__entry">
          <div className="review__entry__line">{todaysReview.happened}</div>
          {!!todaysReview.taught && (
            <div className="review__entry__field">
              <span>taught</span> {todaysReview.taught}
            </div>
          )}
          {!!todaysReview.differently && (
            <div className="review__entry__field">
              <span>differently</span> {todaysReview.differently}
            </div>
          )}
          {!!todaysReview.served && (
            <div className="review__entry__field">
              <span>served</span> {todaysReview.served}
            </div>
          )}
          {!!todaysReview.affirmed && (
            <div className="review__entry__field">
              <span>affirmed</span> {todaysReview.affirmed}
            </div>
          )}
          {canWrite && (
            <button className="btn btn-passive review__revise" onClick={handleRevise}>
              Revise
            </button>
          )}
        </div>
      ) : (
        canWrite && (
          <div className="review__form">
            {REVIEW_QUESTIONS.map((question) => (
              <label className="review__question" key={question.key}>
                <span className="review__question__label">{question.label}</span>
                <input
                  className="textfield"
                  type="text"
                  value={reviewDraft[question.key]}
                  placeholder={question.placeholder}
                  onChange={(event) =>
                    setReviewDraft({ ...reviewDraft, [question.key]: event.target.value })
                  }
                />
              </label>
            ))}

            {record.values.length > 0 && (
              <div className="review__choice">
                <span className="review__choice__prompt">Which value did today serve?</span>
                {record.values.map((value) => (
                  <button
                    className={`review__choice__option${
                      reviewDraft.served === value ? ' review__choice__option--chosen' : ''
                    }`}
                    key={value}
                    onClick={() =>
                      setReviewDraft({
                        ...reviewDraft,
                        served: reviewDraft.served === value ? '' : value,
                      })
                    }
                  >
                    {value}
                  </button>
                ))}
                <span className="review__choice__aside">none is a real answer</span>
              </div>
            )}

            <div className="review__form__row">
              <button
                className={`review__choice__option${
                  reviewDraft.rough ? ' review__choice__option--chosen' : ''
                }`}
                onClick={() => setReviewDraft({ ...reviewDraft, rough: !reviewDraft.rough })}
              >
                today was rough
              </button>
              <button
                className="review__action"
                disabled={!reviewComplete}
                onClick={handleLogReview}
              >
                {revising ? 'Save the revision' : 'Write it down'}
              </button>
            </div>

            {/* §4.3 — the affirmation is a response to a threat, not a daily
                devotion, so it only appears on an evening the writer says was
                rough. That timeliness is the condition the evidence attaches to. */}
            {reviewDraft.rough && (
              <div className="review__affirm">
                <div className="review__affirm__prompt">
                  Then write two lines about why one of your values matters to you. Not about today
                  — about the value.
                </div>
                <input
                  className="textfield"
                  type="text"
                  value={reviewDraft.affirmed}
                  placeholder="Why this matters to me, in my own words"
                  onChange={(event) =>
                    setReviewDraft({ ...reviewDraft, affirmed: event.target.value })
                  }
                />
              </div>
            )}
          </div>
        )
      )}

      {record.reviews.length > (todaysReview ? 1 : 0) && (
        <ul className="review__log">
          {record.reviews
            .filter((entry) => !todaysReview || entry.date !== todaysReview.date)
            .slice(-LOG_SHOWN)
            .reverse()
            .map((entry) => (
              <li className="review__log__entry" key={entry.date}>
                <span className="review__log__date">{entry.date}</span>
                <span>
                  <span className="review__log__line">{entry.happened}</span>
                  {!!entry.differently && (
                    <div className="review__entry__field">
                      <span>differently</span> {entry.differently}
                    </div>
                  )}
                </span>
              </li>
            ))}
        </ul>
      )}

      {/* ── §4.2 Thankfulness, weekly ──────────────────────────────────────── */}

      <div className="review__section-title">
        <span>Thankfulness</span>
        <span className="review__section-title__note">every {gratitude.weekdayLabel}</span>
      </div>

      <div className="review__hint">
        Weekly, not daily, and this is the one setting on the page that is an evidence claim rather
        than a preference: counting blessings <em>once a week</em> raised well-being over six weeks
        where the identical exercise three times a week did not. Doing this daily is how it stops
        working.
      </div>

      <div className={`review__gratitude${gratitude.due ? ' review__gratitude--due' : ''}`}>
        {gratitude.due && canWrite ? (
          <>
            <div className="review__gratitude__prompt">
              Three good things, and why each one happened.
            </div>
            {gratitudeDraft.map((item, index) => (
              <div className="review__gratitude__row" key={index}>
                <input
                  className="textfield"
                  type="text"
                  value={item.thing}
                  placeholder={`Good thing ${index + 1}`}
                  onChange={(event) => {
                    const next = [...gratitudeDraft];
                    next[index] = { ...next[index], thing: event.target.value };
                    setGratitudeDraft(next);
                  }}
                />
                <input
                  className="textfield"
                  type="text"
                  value={item.why}
                  placeholder="why it happened"
                  onChange={(event) => {
                    const next = [...gratitudeDraft];
                    next[index] = { ...next[index], why: event.target.value };
                    setGratitudeDraft(next);
                  }}
                />
              </div>
            ))}
            <button
              className="review__action"
              disabled={!gratitudeComplete}
              onClick={handleLogGratitude}
            >
              Log the week
            </button>
          </>
        ) : (
          <div className="review__gratitude__status">
            {gratitude.lastDate === null
              ? 'Nothing logged yet.'
              : gratitude.loggedToday
              ? `Logged today. Next ${gratitude.weekdayLabel}.`
              : `Last logged ${gratitude.lastDate}, ${gratitude.daysSince} days ago. Next ${gratitude.weekdayLabel}.`}
          </div>
        )}

        {record.thankfulness.length > 0 && (
          <ul className="review__gratitude__log">
            {record.thankfulness
              .slice(-2)
              .reverse()
              .map((entry) => (
                <li key={entry.date}>
                  <span className="review__log__date">{entry.date}</span>
                  <ul className="review__gratitude__items">
                    {entry.items.map((item) => (
                      <li key={item.thing}>
                        {item.thing}
                        {!!item.why && (
                          <span className="review__gratitude__why"> — {item.why}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* ── §4.3 Values ────────────────────────────────────────────────────── */}

      <div className="review__section-title">
        <span>Values</span>
        <span className="review__section-title__note">
          {record.values.length} of {MAX_VALUES}
        </span>
      </div>

      <div className="review__hint">
        Standing, in your own words, edited rarely — they are here to be in front of you while you
        choose tomorrow's one thing, not to be re-derived nightly.
      </div>

      {record.values.length ? (
        <ul className="review__values">
          {record.values.map((value) => (
            <li className="review__values__row" key={value}>
              <span>{value}</span>
              {canWrite && (
                <button
                  className="review__values__remove"
                  title="Remove this value"
                  onClick={() => persist(removeValue(record, value))}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="review__empty">Nothing written down yet.</div>
      )}

      {canWrite && record.values.length < MAX_VALUES && (
        <div className="review__inline-form">
          <input
            className="textfield"
            type="text"
            value={valueDraft}
            placeholder="A value, in your own words"
            onChange={(event) => setValueDraft(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleAddValue()}
          />
          <button className="review__action" disabled={!valueDraft.trim()} onClick={handleAddValue}>
            Add
          </button>
        </div>
      )}

      {/* ── §4.4 Tomorrow's one if-then ────────────────────────────────────── */}

      <div className="review__section-title">
        <span>Tomorrow's one if-then</span>
        <span className="review__section-title__note">the strongest thing here</span>
      </div>

      <div className="review__hint">
        One, not three. The evidence is about forming a <em>specific</em> plan — when, where and how
        — not about planning more of them.
      </div>

      {tally.total > 0 && (
        <div className="review__numbers">
          <strong>{tally.kept}</strong> kept · <strong>{tally.notKept}</strong> not kept ·{' '}
          <strong>{tally.didNotArise}</strong> never came up
          {tally.unanswered > 0 && (
            <>
              {' '}
              · <strong>{tally.unanswered}</strong> unanswered
            </>
          )}
          <span className="review__numbers__note"> — a count of plans, not of you</span>
        </div>
      )}

      {!!todaysPlan && (
        <div className="review__plan">
          <div className="review__plan__sentence">
            <span className="review__plan__keyword">If</span> {todaysPlan.situation}{' '}
            <span className="review__plan__keyword">then I will</span> {todaysPlan.action}
          </div>
          <div className="review__plan__date">set {todaysPlan.date}</div>
        </div>
      )}

      {canWrite && (
        <div className="review__form">
          <div className="review__ifthen">
            <span className="review__plan__keyword">If</span>
            <input
              className="textfield"
              type="text"
              value={planDraft.situation}
              placeholder="the situation — when and where"
              onChange={(event) => setPlanDraft({ ...planDraft, situation: event.target.value })}
            />
          </div>
          <div className="review__ifthen">
            <span className="review__plan__keyword">then I will</span>
            <input
              className="textfield"
              type="text"
              value={planDraft.action}
              placeholder="the one action"
              onChange={(event) => setPlanDraft({ ...planDraft, action: event.target.value })}
            />
          </div>
          <div className="review__form__row">
            <span className="review__hint review__hint--inline">
              Setting one again today replaces today's.
            </span>
            <button
              className="review__action"
              disabled={!planDraft.situation.trim() || !planDraft.action.trim()}
              onClick={handleLogPlan}
            >
              Set it
            </button>
          </div>
        </div>
      )}

      {/* ── §4.5 What keeps this honest ────────────────────────────────────── */}

      <div className="review__section-title">
        <span>What keeps this honest</span>
        <span className="review__section-title__note">a reminder, not a form</span>
      </div>

      <div className="review__cards">
        {HONESTY_CARDS.map((card) => (
          <div className="review__card" key={card.text}>
            <div className="review__card__text">{card.text}</div>
            <div className="review__card__why">{card.why}</div>
            <div className="review__card__strength">{card.strength}</div>
          </div>
        ))}
      </div>

      <div className="review__footer">
        Stored as one <code>* Tagesrückblick :daily-review:</code> node in <code>{path}</code>, one
        commit per change. See <code>doc/daily-review-concept.org</code> for what each section rests
        on — including why there is no streak, no mood score and no resilience number.
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  storedConfig: state.base.get('dashboardConfig'),
  reviewPath: state.base.get('reviewPath') || DEFAULT_REVIEW_PATH,
  gratitudeWeekday:
    state.base.get('gratitudeWeekday') === undefined
      ? DEFAULT_GRATITUDE_WEEKDAY
      : state.base.get('gratitudeWeekday'),
  contractIntegration: state.base.get('reviewContractIntegration') !== false,
});

export default connect(mapStateToProps)(ReviewDashboard);
